import { useState } from 'react';
import { useSession } from '@/lib/session';
import { PLAN_LABEL, type Tier } from '@/lib/access';
import { usePlansCatalog, discountPct, inr } from '@/lib/plansCatalog';
import { payForPlan, razorpayConfigured } from '@/lib/razorpay';

export default function BillingPage() {
  const { restaurant, access, refreshAccess } = useSession();
  const { plans } = usePlansCatalog();
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const configured = razorpayConfigured();

  // Current plan's position in the (rank-ordered) catalog, for downgrade logic.
  const currentIndex = plans.findIndex((p) => p.tier === access.planTier);

  const upgrade = async (tier: string, amount: number, label: string) => {
    setError(null);
    setMessage(null);
    if (!configured) return;
    setBusy(tier);
    try {
      await payForPlan({
        plan: tier as Tier,
        amount,
        restaurantId: restaurant.id,
        onSuccess: async () => {
          await refreshAccess();
          setMessage(`You're now on the ${label} plan. Thank you!`);
        },
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Payment could not be started.');
    } finally {
      setBusy(null);
    }
  };

  const statusLabel = () => {
    switch (access.status) {
      case 'trialing': return 'Free trial';
      case 'active': return access.planTier ? `${PLAN_LABEL[access.planTier]} plan` : 'Active';
      case 'expired': return 'Expired';
      case 'cancelled': return 'Cancelled';
      default: return access.status;
    }
  };

  return (
    <div className="p-6 space-y-5">
      <div>
        <h2 className="text-lg font-bold text-foreground-800">Billing &amp; Plans</h2>
        <p className="text-sm text-foreground-500 mt-0.5">Manage your subscription and upgrade your plan</p>
      </div>

      {message && (
        <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex items-center gap-2">
          <i className="ri-checkbox-circle-line text-green-500"></i>{message}
        </div>
      )}
      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center gap-2">
          <i className="ri-error-warning-line text-red-500"></i>{error}
        </div>
      )}

      {/* Current status */}
      <div className="bg-background-50 border border-background-200 rounded-xl p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-foreground-400">Current status</p>
            <p className="text-base font-semibold text-foreground-800 mt-0.5">{statusLabel()}</p>
          </div>
          {access.status === 'trialing' && (
            <span className="text-sm font-medium text-accent-600 bg-accent-50 rounded-full px-3 py-1">
              {access.daysLeft} {access.daysLeft === 1 ? 'day' : 'days'} left
            </span>
          )}
          {access.status === 'active' && access.currentPeriodEnd && (
            <span className="text-sm text-foreground-500">
              Renews {new Date(access.currentPeriodEnd).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          )}
        </div>
        {access.status === 'trialing' && (
          <p className="text-xs text-foreground-500 mt-2">
            You have full Pro-level access during your trial. Pick a plan before it ends to avoid interruption.
          </p>
        )}
      </div>

      {!configured && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          Online payment isn’t set up yet. Contact support to activate a plan.
        </div>
      )}

      {/* Plans */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {plans.map((plan, idx) => {
          const isCurrent = access.status === 'active' && access.planTier === plan.tier;
          const isDowngrade = currentIndex >= 0 && idx < currentIndex;
          return (
            <div
              key={plan.tier}
              className={`rounded-xl border bg-background-50 p-5 flex flex-col ${isCurrent ? 'border-primary-500 shadow-sm' : plan.popular ? 'border-accent-500' : 'border-background-200'}`}
            >
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-foreground-800">{plan.name}</h3>
                {isCurrent ? (
                  <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-primary-50 text-primary-600">Current</span>
                ) : plan.popular ? (
                  <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-accent-50 text-accent-600">Popular</span>
                ) : null}
              </div>
              <div className="mt-2 flex items-end gap-2">
                <span className="text-2xl font-bold text-foreground-800">{plan.price}</span>
                <span className="text-sm text-foreground-400">{plan.period}</span>
                {discountPct(plan) > 0 && (
                  <span className="text-sm text-foreground-400 line-through mb-0.5">{inr(plan.mrp!)}</span>
                )}
              </div>
              <span className="text-xs text-foreground-400 mt-0.5">per outlet</span>
              {discountPct(plan) > 0 && (
                <span className="mt-1 self-start text-[11px] font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full">{discountPct(plan)}% OFF</span>
              )}
              <p className="text-xs text-foreground-500 mt-1">{plan.description}</p>
              <ul className="mt-4 space-y-1.5 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs text-foreground-600">
                    <i className="ri-check-line text-primary-500 mt-0.5"></i>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => upgrade(plan.tier, plan.priceValue, plan.name)}
                disabled={!configured || busy !== null || isCurrent || isDowngrade}
                className={`mt-5 px-4 py-2 text-sm font-semibold rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${plan.popular ? 'bg-accent-500 text-background-50 hover:bg-accent-600' : 'bg-primary-500 text-background-50 hover:bg-primary-600'}`}
              >
                {isCurrent ? 'Current plan' : busy === plan.tier ? 'Opening checkout…' : isDowngrade ? 'Included' : `Upgrade to ${plan.name}`}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
